import { useSession, useUser } from '@clerk/clerk-react';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useResendEmailVerification, useUpdateUser, useUserDetailQuery } from '@/features/users';
import { normalizeClerkErrorMessage } from '@/pages/auth-error-utils';
import { useResendCooldown } from '@/pages/hooks/useResendCooldown';
import { getMessage } from '@/shared/copy/messages';
import { useCopyNotifications } from '@/shared/copy/notifications';
import { useMutationError } from '@/shared/hooks/useMutationError';
import type { AuthSession } from '@/auth/types';

const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const verificationResendCooldownSeconds = 30;
type ClerkUser = NonNullable<ReturnType<typeof useUser>['user']>;
type PendingEmailAddress = Awaited<ReturnType<ClerkUser['createEmailAddress']>>;
const normaliseEmail = (value: string): string => value.trim().toLowerCase();

const hasClerkErrorCode = (error: unknown, code: string): boolean => {
  if (typeof error !== 'object' || error === null || !('errors' in error)) {
    return false;
  }
  const errors = (error as { errors?: Array<{ code?: string }> }).errors;
  return Array.isArray(errors) && errors.some((entry) => entry.code === code);
};

const normaliseEmailUpdateError = (message: string): string =>
  message.toLowerCase().includes('email address is taken')
    ? getMessage('account.profile.emailAlreadyInUse')
    : message;

export type UserProfileFormState = {
  firstName: string;
  lastName: string;
  email: string;
  panelAlertMessage: string | null;
  emailVerifiedAt: string | null;
  externalAuthProvider: string | null;
  resendVerificationDisabled: boolean;
  emailReadOnly: boolean;
  verificationDialogOpen: boolean;
  verificationCode: string;
  verificationTargetEmail: string;
  verificationError: string | null;
  verificationSubmitting: boolean;
  verificationSendPending: boolean;
  verificationResendOnCooldown: boolean;
  verificationResendSecondsRemaining: number;
  reverificationDialogOpen: boolean;
  reverificationPassword: string;
  reverificationError: string | null;
  reverificationSubmitting: boolean;
  saveDisabled: boolean;
  isLoading: boolean;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onVerificationCodeChange: (value: string) => void;
  onVerificationCancel: () => void;
  onVerificationSubmit: () => void;
  onVerificationResendCode: () => void;
  onReverificationPasswordChange: (value: string) => void;
  onReverificationCancel: () => void;
  onReverificationSubmit: () => void;
  onSave: () => void;
  onResendVerification: () => void;
};

type UserProfileFormModel = {
  state: UserProfileFormState;
  userDetailError: string | null;
  refreshUser: () => Promise<unknown>;
};

export const useUserProfileForm = (session: AuthSession | null): UserProfileFormModel => {
  const { refreshSession } = useAuth();
  const { user: clerkUser } = useUser();
  const { session: clerkSession } = useSession();
  const userDetail = useUserDetailQuery(session?.userId ?? null);
  const updateUser = useUpdateUser();
  const resendVerification = useResendEmailVerification();
  const mutationError = useMutationError();
  const { notifyInfo, notifySuccess } = useCopyNotifications();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationTargetEmail, setVerificationTargetEmail] = useState('');
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [verificationSubmitting, setVerificationSubmitting] = useState(false);
  const [verificationSendPending, setVerificationSendPending] = useState(false);
  const [pendingEmailAddress, setPendingEmailAddress] = useState<PendingEmailAddress | null>(null);
  const [reverificationDialogOpen, setReverificationDialogOpen] = useState(false);
  const [reverificationPassword, setReverificationPassword] = useState('');
  const [reverificationError, setReverificationError] = useState<string | null>(null);
  const [reverificationSubmitting, setReverificationSubmitting] = useState(false);
  const [panelAlertMessage, setPanelAlertMessage] = useState<string | null>(null);
  const pendingReverificationAction = useRef<(() => Promise<void>) | null>(null);
  const {
    secondsRemaining: verificationResendSecondsRemaining,
    onCooldown: verificationResendOnCooldown,
    startCooldown: startVerificationResendCooldown,
  } = useResendCooldown({ seconds: verificationResendCooldownSeconds, tickMs: 250 });
  const [initialProfile, setInitialProfile] = useState<{
    firstName: string;
    lastName: string;
    email: string;
  }>({ firstName: '', lastName: '', email: '' });

  useEffect(() => {
    if (userDetail.data) {
      setFirstName(userDetail.data.firstName ?? '');
      setLastName(userDetail.data.lastName ?? '');
      setEmail(userDetail.data.email);
      setInitialProfile({
        firstName: userDetail.data.firstName ?? '',
        lastName: userDetail.data.lastName ?? '',
        email: userDetail.data.email,
      });
    }
  }, [userDetail.data]);

  const invalidEmail = email.length > 0 && !emailRegex.test(email);
  const clerkManagedEmail = userDetail.data?.externalAuthProvider === 'clerk';
  const emailReadOnly =
    Boolean(userDetail.data?.externalAuthProvider) &&
    userDetail.data?.externalAuthProvider !== 'clerk';
  const emailVerifiedAt = userDetail.data?.emailVerifiedAt
    ? new Date(userDetail.data.emailVerifiedAt).toISOString()
    : null;
  const profileDirty =
    firstName !== initialProfile.firstName ||
    lastName !== initialProfile.lastName ||
    email !== initialProfile.email;
  const saveDisabled = updateUser.isPending || !session || !email || !profileDirty;
  const resendVerificationDisabled = resendVerification.isPending || Boolean(emailVerifiedAt);

  const closeVerificationDialog = (): void => {
    setVerificationDialogOpen(false);
    setVerificationCode('');
    setVerificationError(null);
    setVerificationSubmitting(false);
    setVerificationSendPending(false);
    setVerificationTargetEmail('');
    const orphanedEmailAddress = pendingEmailAddress;
    setPendingEmailAddress(null);
    if (orphanedEmailAddress && orphanedEmailAddress.verification.status !== 'verified') {
      void orphanedEmailAddress.destroy().catch(() => {
        // Ignore cleanup errors for abandoned unverified addresses.
      });
    }
  };

  const openReverificationDialog = (action: () => Promise<void>): void => {
    pendingReverificationAction.current = action;
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationDialogOpen(true);
  };

  const closeReverificationDialog = (): void => {
    pendingReverificationAction.current = null;
    setReverificationDialogOpen(false);
    setReverificationPassword('');
    setReverificationError(null);
    setReverificationSubmitting(false);
  };

  const removePreviousClerkEmailAddress = async (
    previousEmail: string,
    newPrimaryEmailAddressId: string,
  ): Promise<void> => {
    if (!clerkUser) return;
    const previous = clerkUser.emailAddresses.find(
      (entry) =>
        normaliseEmail(entry.emailAddress) === normaliseEmail(previousEmail) &&
        entry.id !== newPrimaryEmailAddressId,
    );
    if (!previous) return;
    if (clerkUser.primaryEmailAddressId === previous.id) return;
    try {
      await previous.destroy();
      await clerkUser.reload();
    } catch {
      notifyInfo('account.profile.removePreviousEmailFailed');
    }
  };

  const beginEmailCodeVerification = async (
    emailAddress: PendingEmailAddress,
    targetEmail: string,
  ): Promise<void> => {
    const preparedEmailAddress = await emailAddress.prepareVerification({
      strategy: 'email_code',
    });
    setPendingEmailAddress(preparedEmailAddress);
    setVerificationTargetEmail(targetEmail);
    setVerificationCode('');
    setVerificationDialogOpen(true);
    setVerificationError(null);
    startVerificationResendCooldown();
    mutationError.clearError();
    setPanelAlertMessage(null);
    notifySuccess('account.profile.verificationCodeSent');
  };

  const findExistingEmailAddress = (targetEmail: string): PendingEmailAddress | null => {
    if (!clerkUser) return null;
    const target = normaliseEmail(targetEmail);
    const match = clerkUser.emailAddresses.find(
      (entry) => normaliseEmail(entry.emailAddress) === target,
    );
    return (match as PendingEmailAddress | undefined) ?? null;
  };

  const resumeExistingUnverifiedEmail = async (targetEmail: string): Promise<boolean> => {
    if (!clerkUser) return false;
    await clerkUser.reload();
    const existing = findExistingEmailAddress(targetEmail);
    if (!existing) return false;
    if (existing.verification.status === 'verified') return false;
    await beginEmailCodeVerification(existing, targetEmail);
    return true;
  };

  const saveProfileToApi = (
    input: { id: string; firstName: string; lastName: string; email: string },
    successMessage: 'account.profile.emailUpdated' | 'account.profile.profileUpdated',
  ): void => {
    updateUser.mutate(input, {
      onSuccess: () => {
        mutationError.clearError();
        setPanelAlertMessage(null);
        notifySuccess(successMessage);
        void Promise.all([refreshSession(), userDetail.refetch()]);
      },
      onError: (err) => {
        mutationError.setError(err);
        setPanelAlertMessage(
          normaliseEmailUpdateError(
            normalizeClerkErrorMessage(err, getMessage('account.profile.updateFailed')),
          ),
        );
      },
    });
  };

  const startClerkEmailUpdate = async (nextEmail: string): Promise<void> => {
    if (!clerkManagedEmail || !session) {
      return;
    }
    if (!clerkUser) {
      const message = getMessage('account.profile.authSessionLoading');
      mutationError.setError(new Error(message));
      setPanelAlertMessage(message);
      return;
    }
    setVerificationSendPending(true);
    setVerificationError(null);
    try {
      const existing = findExistingEmailAddress(nextEmail);
      if (existing && existing.verification.status !== 'verified') {
        await beginEmailCodeVerification(existing, nextEmail);
        return;
      }

      const createdEmailAddress = await clerkUser.createEmailAddress({ email: nextEmail });
      await beginEmailCodeVerification(createdEmailAddress, nextEmail);
    } catch (error) {
      if (hasClerkErrorCode(error, 'session_reverification_required')) {
        openReverificationDialog(async () => {
          await startClerkEmailUpdate(nextEmail);
        });
        return;
      }
      if (await resumeExistingUnverifiedEmail(nextEmail)) {
        return;
      }
      if (hasClerkErrorCode(error, 'reverification_cancelled')) {
        mutationError.clearError();
        notifyInfo('account.profile.updateCancelled');
        setVerificationError(null);
        return;
      }
      const message = normalizeClerkErrorMessage(
        error,
        getMessage('account.profile.startVerificationFailed'),
      );
      const friendlyMessage = normaliseEmailUpdateError(message);
      mutationError.setError(new Error(friendlyMessage));
      setPanelAlertMessage(friendlyMessage);
      setVerificationError(friendlyMessage);
    } finally {
      setVerificationSendPending(false);
    }
  };

  const handleSaveProfile = (): void => {
    if (!session) return;
    setPanelAlertMessage(null);
    if (invalidEmail) {
      const message = getMessage('account.profile.invalidEmail');
      mutationError.setError(new Error(message));
      setPanelAlertMessage(message);
      return;
    }
    const trimmedEmail = email.trim();
    const emailChanged = trimmedEmail !== initialProfile.email;
    if (emailChanged && clerkManagedEmail) {
      void startClerkEmailUpdate(trimmedEmail);
      return;
    }

    saveProfileToApi(
      { id: session.userId, firstName, lastName, email: trimmedEmail },
      'account.profile.profileUpdated',
    );
  };

  const handleVerificationSubmit = (): void => {
    if (!session || !clerkUser || !pendingEmailAddress) return;
    const code = verificationCode.trim();
    if (!code) {
      setVerificationError(getMessage('account.profile.verificationCodeRequired'));
      return;
    }

    const persistProfileAfterVerification = (): void => {
      const namesChanged =
        firstName !== initialProfile.firstName || lastName !== initialProfile.lastName;
      if (!namesChanged) {
        mutationError.clearError();
        setPanelAlertMessage(null);
        notifySuccess('account.profile.emailUpdated');
        void Promise.all([refreshSession(), userDetail.refetch()]);
        closeVerificationDialog();
        return;
      }
      updateUser.mutate(
        {
          id: session.userId,
          firstName,
          lastName,
        },
        {
          onSuccess: () => {
            mutationError.clearError();
            setPanelAlertMessage(null);
            notifySuccess('account.profile.profileUpdated');
            void Promise.all([refreshSession(), userDetail.refetch()]);
            closeVerificationDialog();
          },
          onError: (err) => {
            mutationError.setError(err);
            setPanelAlertMessage(
              normaliseEmailUpdateError(
                normalizeClerkErrorMessage(err, getMessage('account.profile.updateFailed')),
              ),
            );
          },
        },
      );
    };

    setVerificationSubmitting(true);
    setVerificationError(null);
    void pendingEmailAddress
      .attemptVerification({ code })
      .then(async (attemptedAddress: PendingEmailAddress) => {
        if (attemptedAddress.verification.status !== 'verified') {
          setVerificationError(getMessage('account.profile.verificationDidNotComplete'));
          return;
        }
        try {
          const previousPrimaryEmail = initialProfile.email;
          await clerkUser.update({ primaryEmailAddressId: attemptedAddress.id });
          await clerkUser.reload();
          if (
            normaliseEmail(previousPrimaryEmail) !== normaliseEmail(attemptedAddress.emailAddress)
          ) {
            await removePreviousClerkEmailAddress(previousPrimaryEmail, attemptedAddress.id);
          }
          persistProfileAfterVerification();
        } catch (error: unknown) {
          if (hasClerkErrorCode(error, 'session_reverification_required')) {
            openReverificationDialog(async () => {
              await clerkUser.update({ primaryEmailAddressId: attemptedAddress.id });
              await clerkUser.reload();
              persistProfileAfterVerification();
            });
            return;
          }
          setVerificationError(
            normalizeClerkErrorMessage(error, getMessage('account.profile.updatePrimaryFailed')),
          );
        }
      })
      .catch((error: unknown) => {
        setVerificationError(
          normalizeClerkErrorMessage(error, getMessage('account.profile.verificationFailed')),
        );
      })
      .finally(() => {
        setVerificationSubmitting(false);
      });
  };

  const handleReverificationSubmit = (): void => {
    const password = reverificationPassword.trim();
    if (!password) {
      setReverificationError(getMessage('auth.common.passwordRequired'));
      return;
    }
    if (!clerkSession) {
      setReverificationError(getMessage('auth.common.authSessionLoading'));
      return;
    }

    setReverificationSubmitting(true);
    setReverificationError(null);
    void clerkSession
      .startVerification({ level: 'first_factor' })
      .then(async () => {
        const verification = await clerkSession.attemptFirstFactorVerification({
          strategy: 'password',
          password,
        });
        if (verification.status !== 'complete') {
          throw new Error(getMessage('auth.common.reverificationAdditionalRequired'));
        }
        const resumeAction = pendingReverificationAction.current;
        closeReverificationDialog();
        if (resumeAction) {
          await resumeAction();
        }
      })
      .catch((error: unknown) => {
        if (hasClerkErrorCode(error, 'reverification_cancelled')) {
          closeReverificationDialog();
          mutationError.clearError();
          setPanelAlertMessage(null);
          notifyInfo('account.profile.updateCancelled');
          return;
        }
        setReverificationError(
          normalizeClerkErrorMessage(error, getMessage('auth.common.reverificationFailed')),
        );
      })
      .finally(() => {
        setReverificationSubmitting(false);
      });
  };

  const handleVerificationResendCode = (): void => {
    if (!pendingEmailAddress) return;
    if (verificationResendOnCooldown) return;
    setVerificationSendPending(true);
    setVerificationError(null);
    void pendingEmailAddress
      .prepareVerification({ strategy: 'email_code' })
      .then(() => {
        startVerificationResendCooldown();
        notifySuccess('account.profile.verificationCodeSent');
      })
      .catch((error: unknown) => {
        setVerificationError(
          normalizeClerkErrorMessage(error, getMessage('account.profile.resendVerificationFailed')),
        );
      })
      .finally(() => {
        setVerificationSendPending(false);
      });
  };

  const handleResendVerification = (): void => {
    resendVerification.mutate(undefined, {
      onSuccess: (result) => {
        mutationError.clearError();
        if (result.status === 'already-verified') {
          notifySuccess('account.profile.emailAlreadyVerified');
          return;
        }
        notifySuccess('account.profile.verificationEmailSent');
        void Promise.all([refreshSession(), userDetail.refetch()]);
      },
      onError: (err) => {
        mutationError.setError(err);
      },
    });
  };

  return {
    state: {
      firstName,
      lastName,
      email,
      panelAlertMessage,
      emailVerifiedAt,
      externalAuthProvider: userDetail.data?.externalAuthProvider ?? null,
      resendVerificationDisabled,
      emailReadOnly,
      verificationDialogOpen,
      verificationCode,
      verificationTargetEmail,
      verificationError,
      verificationSubmitting,
      verificationSendPending,
      verificationResendOnCooldown,
      verificationResendSecondsRemaining,
      reverificationDialogOpen,
      reverificationPassword,
      reverificationError,
      reverificationSubmitting,
      saveDisabled,
      isLoading: userDetail.isFetching,
      onFirstNameChange: (value) => {
        setFirstName(value);
        setPanelAlertMessage(null);
      },
      onLastNameChange: (value) => {
        setLastName(value);
        setPanelAlertMessage(null);
      },
      onEmailChange: (value) => {
        setEmail(value);
        setPanelAlertMessage(null);
      },
      onVerificationCodeChange: setVerificationCode,
      onVerificationCancel: closeVerificationDialog,
      onVerificationSubmit: handleVerificationSubmit,
      onVerificationResendCode: handleVerificationResendCode,
      onReverificationPasswordChange: setReverificationPassword,
      onReverificationCancel: closeReverificationDialog,
      onReverificationSubmit: handleReverificationSubmit,
      onSave: handleSaveProfile,
      onResendVerification: handleResendVerification,
    },
    userDetailError: userDetail.error?.message ?? null,
    refreshUser: userDetail.refetch,
  };
};
